import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import ContactForm from '@/components/sections/ContactForm';
import { Calendar, ArrowRight, Clock, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const { lang, t } = useLanguage();
  const prefix = lang === 'en' ? '/en' : '';
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/blog?lang=${lang}`);
        setArticles(response.data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [lang]);

  const title = t(
    'Blog - Conseils Nettoyage & Conciergerie | VS Riviera',
    'Blog - Cleaning & Concierge Tips | VS Riviera'
  );

  const description = t(
    "Conseils et astuces pour le nettoyage, la gestion Airbnb et la conciergerie sur la Côte d'Azur.",
    'Tips and tricks for cleaning, Airbnb management and concierge services on the French Riviera.'
  );

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <link rel="canonical" href={`${window.location.origin}${prefix}/blog`} />
      </Helmet>

      <Header />
      
      <main data-testid="blog-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                {t('Notre Blog', 'Our Blog')}
              </h1>
              <p className="text-slate-300 text-lg">
                {t(
                  "Découvrez nos conseils d'experts sur le nettoyage, la gestion locative et la conciergerie sur la Côte d'Azur.",
                  'Discover our expert tips on cleaning, property management and concierge services on the French Riviera.'
                )}
              </p>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="h-48 bg-slate-200 rounded-xl mb-6" />
                    <div className="h-6 bg-slate-200 rounded mb-4" />
                    <div className="h-4 bg-slate-200 rounded mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600 text-lg">
                  {t('Aucun article pour le moment.', 'No articles yet.')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`${prefix}/blog/${article.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
                    data-testid={`blog-article-card-${article.slug}`}
                  >
                    {/* Image */}
                    <div className="h-48 bg-gradient-to-br from-amber-400 to-amber-600 relative overflow-hidden">
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
                      <div className="absolute bottom-4 left-4">
                        <span className="bg-white/90 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
                          {article.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-slate-600 mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>

                      {/* Meta */}
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(article.date).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {article.readTime} min
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-amber-600 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default BlogPage;
